import React, { useState, useEffect } from "react";
import axios from "axios";
import "./OrderManagement.css";
import { toast } from "react-hot-toast";

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(
        "https://api.swasthmitra.com/api/transaction/getAllOrders"
      );
      setOrders(response.data.orders);
      const initialStatus = {};
      response.data.orders.forEach((order) => {
        initialStatus[order._id] = order.status;
      });
      setSelectedStatus(initialStatus);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(
        `https://api.swasthmitra.com/api/transaction/updateStatus/${orderId}`,
        {
          status: newStatus,
        }
      );

      setSelectedStatus((prev) => ({
        ...prev,
        [orderId]: newStatus,
      }));

      toast.success("Order status updated successfully");
      fetchOrders();
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const OrderDetailsModal = ({ order, onClose }) => {
    if (!order) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Order Details</h3>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="modal-body">
            <div className="order-info">
              <p>
                <strong>Order ID:</strong> {order.orderNumber}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {new Date(order.createdAt).toLocaleString()}
              </p>
              <p>
                <strong>Status:</strong> {selectedStatus[order._id]}
              </p>
              <p>
                <strong>Total Amount:</strong> ₹{order.total}
              </p>
            </div>
            
            <div className="customer-info">
              <h4>Customer Details</h4>
              <div className="customer-details">
                <p><strong>Name:</strong> {order.userId?.name || "N/A"}</p>
                <p><strong>Mobile:</strong> {order.userId?.MobileNumber || "N/A"}</p>
                <p><strong>Email:</strong> {order.userId?.email || "N/A"}</p>
                {order.userId?.location?.coordinates && (
                  <p>
                    <strong>Location:</strong>{" "}
                    <a 
                      href={`https://www.google.com/maps?q=${order.userId.location.coordinates[1]},${order.userId.location.coordinates[0]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View on Map
                    </a>
                  </p>
                )}  
              </div>
            </div>
            
            {/* Delivery Address Section */}
            {order.deliveryAddress && (
              <div className="delivery-info">
                <h4>Delivery Address</h4>
                <div className="delivery-details">
                  <p><strong>Deliver To:</strong> {order.deliveryAddress.fullName}</p>
                  <p><strong>Phone:</strong> {order.deliveryAddress.phoneNumber}</p>
                  <p><strong>Address:</strong> {order.deliveryAddress.addressLine1}</p>
                  {order.deliveryAddress.addressLine2 && (
                    <p><strong>Address Line 2:</strong> {order.deliveryAddress.addressLine2}</p>
                  )}
                  <p><strong>City:</strong> {order.deliveryAddress.city}</p>
                  <p><strong>State:</strong> {order.deliveryAddress.state}</p>
                  <p><strong>Postal Code:</strong> {order.deliveryAddress.postalCode}</p>
                  {order.deliveryAddress.landmark && (
                    <p><strong>Landmark:</strong> {order.deliveryAddress.landmark}</p>
                  )}
                </div>
              </div>
            )}
            
            <div className="order-items">
              <h4>Ordered Items</h4>
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Pack Size</th>
                    <th>Batch</th>
                    <th>Quantity</th>
                    <th>MRP</th>
                    <th>GST</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {order.orderItems.map((item, index) => (
                    <tr key={index}>
                      <td>{item.productName}</td>
                      <td>{item.packof}</td>
                      <td>{item.batchNumber}</td>
                      <td>{item.quantity}</td>
                      <td>₹{item.mrp}</td>
                      <td>{item.gst}%</td>
                      <td>
                        ₹
                        {(
                          item.subtotal +
                          item.subtotal * (item.gst / 100)
                        ).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="order-summary">
              <p>
                <strong>Subtotal:</strong> ₹{order.subtotal}
              </p>
              <p>
                <strong>Shipping:</strong> ₹{order.shippingCost}
              </p>
              <p>
                <strong>VAT:</strong> ₹{order.vat}
              </p>
              <p className="total">
                <strong>Total:</strong> ₹{order.total}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="order-management">
      <h2>Order Management</h2>
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Total</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order._id}>
                <td>
                  <button
                    className="order-id-button"
                    onClick={() => handleOrderClick(order)}
                  >
                    {order.orderNumber}
                  </button>
                </td>
                <td>
                  <button
                    className="customer-name-button"
                    onClick={() => handleOrderClick(order)}
                  >
                    {order.userId?.name || "N/A"}
                  </button>
                </td>
                <td>{new Date(order.createdAt).toLocaleString()}</td>
                <td>₹{order.total}</td>
                <td>
                  <span className={`status-badge ${selectedStatus[order._id]}`}>
                    {selectedStatus[order._id]}
                  </span>
                </td>
                <td>
                  <select
                    value={selectedStatus[order._id]}
                    onChange={(e) =>
                      handleStatusChange(order._id, e.target.value)
                    }
                    className="status-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showOrderDetails && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
};

export default OrderManagement;
